import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { CheckCircle2, AlertTriangle } from 'lucide-react';
import ProgrammeDialog from './ProgrammeDialog';
import { tickMilestone } from '../../redux/programmeSlice';
import { LIMITS } from '../../utils/careProgrammes';
import { serverReason } from '../patientTabs/prescriptionHelpers';

// TICK A MILESTONE BY HAND — for what was done without a reading or a
// completed visit here (a scan at the lab across the road). An optional note
// says how it was done; it is kept with the tick and shown in the record.
function TickMilestoneModal({ isOpen, patientId, programme, milestone, onClose }) {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const busy = useSelector((state) => Boolean(programme && state.programmes?.acting === programme.patientProgrammeId));
    const [note, setNote] = useState('');
    const [serverError, setServerError] = useState('');

    useEffect(() => {
        if (!isOpen) return;
        setNote('');
        setServerError('');
    }, [isOpen, milestone?.key]);

    if (!programme || !milestone) return null;

    const submit = async () => {
        if (busy) return;
        setServerError('');
        const res = await dispatch(tickMilestone({
            patientId,
            patientProgrammeId: programme.patientProgrammeId,
            key: milestone.key,
            done: true,
            note,
        }));
        if (tickMilestone.fulfilled.match(res)) {
            toast.success(t('programmes.ticked', { name: milestone.name }));
            onClose?.();
            return;
        }
        setServerError(serverReason(res.payload, t('programmes.tick_failed'), i18n.language));
    };

    return (
        <ProgrammeDialog
            isOpen={isOpen}
            testId="tick-milestone"
            title={t('programmes.tick_title', { name: milestone.name })}
            icon={CheckCircle2}
            onClose={onClose}
            onSubmit={submit}
            submitLabel={t('programmes.tick_confirm')}
            busy={busy}
        >
            <p className="text-sm text-gray-600">{t('programmes.tick_text', { programme: programme.name })}</p>
            <div>
                <label htmlFor="tick-milestone-note" className="mb-1 block text-xs font-semibold text-gray-600">{t('programmes.tick_note_label')}</label>
                <textarea
                    id="tick-milestone-note"
                    data-testid="tick-milestone-note"
                    rows={3}
                    maxLength={LIMITS.note}
                    value={note}
                    placeholder={t('programmes.tick_note_placeholder')}
                    onChange={(e) => { setNote(e.target.value); setServerError(''); }}
                    className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-[#4ABDBD] focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/40"
                />
                <p className="mt-1 text-right text-[11px] text-gray-400">{note.length}/{LIMITS.note}</p>
            </div>
            {serverError && (
                <p role="alert" data-testid="tick-milestone-error" className="flex items-center gap-2 rounded-xl border border-red-100 bg-red-50 p-3 text-xs font-medium text-red-700">
                    <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
                    {serverError}
                </p>
            )}
        </ProgrammeDialog>
    );
}

export default TickMilestoneModal;
